import React from "react";
import { Checkbox, FormField, Input, NativeSelect } from "../ui";

export const DEFAULT_UNLOCK = {
  previousSessionCompleted: true,
  requireAttendanceOrRecording: false,
  engagementPercent: 60,
  assignmentRequired: false,
  minQuizScore: 0,
};

export const UNLOCK_MODES = [
  { value: "onStart", label: "When the class starts", hint: "Students can open them during the class and ask doubts." },
  { value: "afterClass", label: "When the class ends", hint: "They open as soon as the trainer ends the class." },
  { value: "manual", label: "When the trainer unlocks them", hint: "They stay hidden until the trainer chooses." },
];

const pct = (v) => Math.min(100, Math.max(0, Number(v) || 0));

/** The rules as the server takes them — numbers clamped, flags boolean. */
export const cleanUnlockConditions = (u = {}) => ({
  previousSessionCompleted: u.previousSessionCompleted !== false,
  requireAttendanceOrRecording: !!u.requireAttendanceOrRecording,
  engagementPercent: pct(u.engagementPercent ?? 60) || 60,
  assignmentRequired: !!u.assignmentRequired,
  minQuizScore: pct(u.minQuizScore),
});

/**
 * When a session's resources open, and what a student must do before the NEXT
 * session opens. Controlled: `value` is { unlockMode, unlockConditions }.
 *
 * Props: value, onChange(next), disabled, idPrefix
 */
const UnlockRulesFields = ({ value, onChange, disabled = false }) => {
  const mode = value?.unlockMode || "afterClass";
  const u = { ...DEFAULT_UNLOCK, ...(value?.unlockConditions || {}) };
  const setRule = (patch) => onChange({ ...value, unlockConditions: { ...u, ...patch } });
  const modeHint = UNLOCK_MODES.find((m) => m.value === mode)?.hint;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <FormField label="Resources open" hint={modeHint}>
        <NativeSelect
          value={mode}
          onChange={(e) => onChange({ ...value, unlockMode: e.target.value })}
          disabled={disabled}
          options={UNLOCK_MODES.map(({ value: v, label }) => ({ value: v, label }))}
        />
      </FormField>

      <fieldset className="tw-m-0 tw-flex tw-flex-col tw-gap-2.5 tw-border-0 tw-p-0">
        <legend className="tw-mb-1 tw-p-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
          The next session opens for a student only when…
        </legend>
        <Checkbox
          checked={u.previousSessionCompleted}
          onChange={(e) => setRule({ previousSessionCompleted: e.target.checked })}
          disabled={disabled}
          label="the trainer has finished this session"
        />
        <Checkbox
          checked={u.requireAttendanceOrRecording}
          onChange={(e) => setRule({ requireAttendanceOrRecording: e.target.checked })}
          disabled={disabled}
          label="they attended it or watched its recording"
          description="Attending live is enough; a student who missed it can catch up from the recording."
        />
        {u.requireAttendanceOrRecording && (
          <div className="tw-ps-7">
            <FormField label="For at least this % of the class">
              <Input
                type="number"
                min={0}
                max={100}
                fullWidth={false}
                className="tw-w-28"
                value={u.engagementPercent}
                onChange={(e) => setRule({ engagementPercent: e.target.value })}
                disabled={disabled}
              />
            </FormField>
          </div>
        )}
        <Checkbox
          checked={u.assignmentRequired}
          onChange={(e) => setRule({ assignmentRequired: e.target.checked })}
          disabled={disabled}
          label="they handed in its assignment"
        />
        <FormField label="…and they scored at least this % on its quiz" hint="0 means any score.">
          <Input
            type="number"
            min={0}
            max={100}
            fullWidth={false}
            className="tw-w-28"
            value={u.minQuizScore}
            onChange={(e) => setRule({ minQuizScore: e.target.value })}
            disabled={disabled}
          />
        </FormField>
      </fieldset>
    </div>
  );
};

export default UnlockRulesFields;
